import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  cancelSubscription,
  getSubscriptionDetail,
  setSubscriptionData,
} from "../redux/features/SubscriptionService";
import {
  applyCancelledSubscriptionData,
  mergeSubscriptionData,
} from "../utils/subscriptionStatus";

const useSubscriptionCancelRefresh = ({ userId, isAdminUser = false }) => {
  const dispatch = useDispatch();
  const [viewData, setViewData] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const hasCancelledLocallyRef = useRef(false);
  const { subscriptionData, loading } = useSelector((state) => state.subscription);

  useEffect(() => {
    hasCancelledLocallyRef.current = false;
    dispatch(getSubscriptionDetail({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (subscriptionData && !hasCancelledLocallyRef.current) {
      setViewData(subscriptionData);
    }
  }, [subscriptionData]);

  const updateSubscriptionView = (data) => {
    setViewData(data);
    dispatch(setSubscriptionData(data));
  };

  const handleCancelSubscription = async () => {
    const { stripeSubscriptionId } = viewData?.subscriptionDetail || {};
    if (!stripeSubscriptionId || isCancelling) {
      return;
    }

    setIsCancelling(true);
    try {
      const res = await dispatch(
        cancelSubscription({ stripeSubscriptionId, userId, isAdminUser })
      ).unwrap();

      const updatedView = applyCancelledSubscriptionData(
        res?.subscriptionData || viewData,
        { isAdminUser }
      );
      hasCancelledLocallyRef.current = true;
      updateSubscriptionView(updatedView);

      toast.success(
        res?.message || "Subscription cancelled successfully",
        { autoClose: 3000 }
      );

      try {
        const refreshed = await dispatch(
          getSubscriptionDetail({ userId, _refresh: Date.now() })
        ).unwrap();
        if (refreshed?.subscriptionData) {
          const refreshedView = mergeSubscriptionData(
            updatedView,
            applyCancelledSubscriptionData(refreshed.subscriptionData, {
              isAdminUser,
            })
          );
          updateSubscriptionView(refreshedView);
        }
      } catch {
        // Keep optimistic view if background refresh fails.
      }
    } catch {
      toast.error("Failed to cancel subscription. Please try again.", {
        autoClose: 3000,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return {
    viewData,
    loading,
    isCancelling,
    handleCancelSubscription,
  };
};

export default useSubscriptionCancelRefresh;
